/**
 * Auth service
 */

import moment from 'moment'
import { YM_COUNTER } from '../../app/core/constants'
import * as Sentry from '@sentry/browser'

/**
 * E401Svc service
 * handle 401 responses
 *
 * @class e401Svc
 * @memberof mwApp/auth
 * @see mwApp/auth
 *
 * @ngdoc service
 * @name mwApp/auth.service:e401Svc
 */
/* @ngInject */
export function E401Svc ($rootScope, $state, storageSvc, notifySvc) {
  /**
   * Array of requests, required authentication
   *
   * @private
   * @memberof mwApp/auth.service:e401Svc~
   * @type {Array}
   */
  const requests401 = []

  /**
   * Update time of last successful request to api
   *
   * @memberof mwApp/auth.service:e401Svc
   * @param {Object} response - http response
   */
  this.updateLastAccess = function (response) {
    if (response.config && response.config.url && response.config.url.indexOf('api/') === 0) {
      storageSvc.local.set('last', Date.now())
    }
  }

  /**
   * Check time of last successful request
   *
   * @memberof mwApp/auth.service:e401Svc
   * @return {Boolean} true if last request was less than 1 hour ago
   */
  this.checkLastAccess = function () {
    const last = +storageSvc.local.get('last')
    return (Date.now() - last < 3600000) // 1 hour
  }

  /**
   * Add 401 request to postponed queue
   *
   * @memberof mwApp/auth.service:e401Svc
   * @param {Promise} deferred - postponed request
   * @param {Object} rejection - http rejection
   */
  this.add = function (deferred, rejection) {
    rejection.data = null // to avoid error alerts on login screen
    requests401.push({
      rejection: rejection,
      deferred: deferred
    })
  }

  /**
   * Retry all postponed requests
   *
   * @memberof mwApp/auth.service:e401Svc
   * @param  {Object} $http - $http service from AngularJS (we can't use injection because of circular dependencies)
   */
  this.retryAll = function ($http) {
    requests401.forEach(
      req =>
        $http(req.rejection.config)
          .then(
            response =>
              req.deferred.resolve(response)
          )
    )
    requests401.length = 0
  }

  /**
   * Return 'authenticated' flag from storage
   *
   * @memberof mwApp/auth.service:authSvc
   */
  this.isAuthenticated = function () {
    return storageSvc.local.get('authenticated', false)
  }

  /**
   * Reject all postponed requests
   *
   * @memberof mwApp/auth.service:e401Svc
   */
  this.rejectAll = function () {
    requests401.forEach(
      req =>
        req.deferred.reject(req.rejection)
    )
    requests401.length = 0

    // set user as not authenticated
    // same as authSvc.setAuthenticated(false), which we cannot use here bacause of circular dependencies...
    $rootScope.authenticated = false
    storageSvc.local.remove('authenticated')
    storageSvc.local.remove('user')
    storageSvc.local.remove('features')
    notifySvc.clear()
  }
}

/**
 * AuthSvc service
 * do login, logout and so on
 *
 * @class authSvc
 * @memberof mwApp/auth
 * @see mwApp/auth
 * @emits mwApp.event:changePasswordRequired
 *
 * @ngdoc service
 * @name mwApp/auth.service:authSvc
 */
/* @ngInject */
export function AuthSvc ($rootScope, $http, $q, $httpParamSerializerJQLike, $state, storageSvc, keycloakAdapterSvc, User, accessParser, featureFlagsParser) {
  /* === private === */

  /**
   * Storage to save session data
   *
   * @private
   * @memberof mwApp/auth.service:authSvc~
   * @type {mwApp/common.service:storageSvc}
   */
  const storage = storageSvc.local // use localStorage to save session data

  /**
   * Session user
   *
   * @private
   * @memberof mwApp/auth.service:authSvc~
   * @type {models.User}
   */
  let user = null

  /**
   * Timezone request promise
   *
   * @private
   * @memberof mwApp/auth.service:authSvc~
   * @type {Promise}
   */
  let whenTimezone

  /* === public === */

  this.init = init
  this.timezone = timezone
  this.getUser = getUser
  this.getSystemInfo = getSystemInfo
  this.setUser = setUser
  this.isAuthenticated = isAuthenticated
  this.setAuthenticated = setAuthenticated
  this.doLogin = doLogin
  this.setSessionData = sendSessionData
  this.doLogout = doLogout
  this.restorePassword = restorePassword
  this.changePassword = changePassword
  this.hasAccess = hasAccess
  this.hasEnabledFlag = hasEnabledFlag
  this.resetPassword = resetPassword
  this.resetTokenConfirm = resetTokenConfirm
  this.manageFeatureFlags = manageFeatureFlags
  this.manageAgeRatings = manageAgeRatings
  this.manageCurrencies = manageCurrencies

  // Credentials API
  this.getCredentials = getCredentials
  this.saveCredentials = saveCredentials
  this.resetCredentials = resetCredentials

  // KeyCloack
  this.hasKeycloak = hasKeycloak
  this.doKeycloakLogin = doKeycloakLogin
  this.keycloakInitEventSubscribe = keycloakInitEventSubscribe
  /* === implementation === */

  /**
   * Get system info
   *
   * @memberof mwApp/auth.service:authSvc
   * @param {Boolean} [force] - force HTTP request to API backend
   * @return {Promise|Object} if (force === true) return promise; else return user object
   */
  function getSystemInfo (force = false) {
    return force
      ? $http.get('api/info/system').data()
        .then(
          systemInfo => {
            storage.set('systemInfo', systemInfo)
          }
        )
      : storage.get('systemInfo', null)
  }

  /**
   * Get User object
   *
   * @memberof mwApp/auth.service:authSvc
   * @param {Boolean} [force] - force HTTP request to API backend
   * @return {Promise|models.User} if (force === true) return promise; else return user object
   */
  function getUser (force = false) {
    return force
      ? User.loadPublic('api/login/user')
        .then(
          user => setUser(user, true),
          () => setAuthenticated(false)
        )
      : user || new User(storage.get('user', null))
  }

  /**
   * Set User object
   *
   * @memberof mwApp/auth.service:authSvc
   * @param {models.User} u - user information
   * @param {Boolean} checkPwdChange
   */
  function setUser (u, checkPwdChange) {
    user = u
    setAuthenticated(user)
    accessParser.setRules(user ? user.rule : undefined)

    // if 'user' object flag 'pwdChange' set to 'true' - server requires to change password
    // emit event:changePasswordRequired
    if (checkPwdChange && user.pwdChange) {
      /**
       * Event occurs when backend return require for user to change password <br>
       * Fires in {@link mwApp/auth.service:authSvc} service
       *
       * @event event:changePasswordRequired
       * @memberof mwApp
       */
      $rootScope.$broadcast('event:changePasswordRequired')
    }
  }

  /**
   * Set user as authenticated (or not)
   *
   * @memberof mwApp/auth.service:authSvc
   * @param {Object} user - user information
   */
  function setAuthenticated (user) {
    if (user) {
      $rootScope.authenticated = true
      storage.set('authenticated', true)
      storage.set('user', user._object())
      storage.set('userType', user._object().userType)
    } else {
      $rootScope.authenticated = false
      storage.remove('authenticated')
      storage.remove('user')
      storage.remove('KEYCLOAK_TOKEN')
    }
  }

  /**
   * Return 'authenticated' flag from storage
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function isAuthenticated () {
    return storage.get('authenticated', false)
  }

  /**
   * Init timezone request
   * Get server timezone AND new xsrf token
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function timezone () {
    whenTimezone =
      $http.get('api/info/timezone')
        .then(
          response => {
            const { name, offset } = response.data // => { name: 'Europe/Minsk', offset: '+03:00' }
            let timezoneOffset = ''
            if (offset[0] === '-') {
              timezoneOffset += offset[0]
            }
            if (offset[1] === '0') {
              timezoneOffset += offset[2]
            } else {
              timezoneOffset += offset[1]
              timezoneOffset += offset[2]
            }
            timezoneOffset = parseInt(timezoneOffset)
            storage.set('timezone', name) // save timezone to storage
            storage.set('timezoneOffset', timezoneOffset) // save timezone to storage
            moment.tz.setDefault(name) // set default timezone for moment.js
          }
        )
    return whenTimezone
  }

  /**
   * Init Auth service
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function init () {
    user = getUser()
    if ((!user || !user.id) && isAuthenticated()) {
      user = getUser(true)
    } else {
      setAuthenticated(user && isAuthenticated() ? user : false)
      accessParser.setRules(user ? user.rule : undefined)
    }
    manageFeatureFlags()
    manageAgeRatings()
    manageCurrencies()
    $rootScope.sendSessionData()
  }

  /**
   * Perform login request to API
   *
   * @memberof mwApp/auth.service:authSvc
   * @param {Object} user - user object with fields 'username' and 'password'
   * @param {String} user.username - user's name
   * @param {String} user.password - user's password
   * @return {Promise} promise
   */
  function doLogin (user) {
    const deferred = $q.defer()

    whenTimezone
      .then(
        () => // do auth request
          $http.post('auth', $httpParamSerializerJQLike(user), { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
      )
      .then(
        response => {
          // try to save credentials
          saveCredentials(user)
            .catch((err) => console.warn(err))

          // get user from server
          getUser(true)
            .then(() => deferred.resolve(response)) // return parent response
            .catch(() => deferred.reject(response))

          getSystemInfo(true)
          manageFeatureFlags(true)
          manageAgeRatings(true)
          manageCurrencies(true)
        }
      )
      .catch(
        response => {
          setAuthenticated(false)
          deferred.reject(response)
        }
      )

    return deferred.promise
  }

  /**
   * Perform login to keycloak
   *
   * @memberof mwApp/auth.service:authSvc
   * @return {Promise} promise
   */
  function doKeycloakLogin () {
    const deferred = $q.defer()
    whenTimezone
      .then(
        response => {
          // get user from server
          getUser(true)
            .then(() => deferred.resolve(response)) // return parent response
            .catch(() => deferred.reject(response))

          getSystemInfo(true)
          manageFeatureFlags(true)
          manageAgeRatings(true)
          manageCurrencies(true)
        }
      )
      .catch(
        response => {
          setAuthenticated(false)
          deferred.reject(response)
        }
      )

    return deferred.promise
  }

  function sendSessionData () {
    let wasSend = false
    /**
     * Send Yandex Metrika session info
     *
     * @param id
     * @param email
     * @param PID
     */
    const setYM = (id, email, PID) => {
      if ($rootScope.enabledYM) {
        window.ym(YM_COUNTER, 'userParams', { id, email, PID })
      }
    }

    /**
     * Send Sentry session info
     *
     * @param id
     * @param email
     * @param PID
     */
    const setSentry = (id, email, PID) => {
      Sentry.configureScope(scope => {
        scope.setUser({ email, id })
        scope.setTags({ PID })
      })
    }
    return () => {
      if (wasSend) { return }
      const { email, id } = getUser() || {}
      const { PID } = getSystemInfo() || {}

      if (id || PID) {
        wasSend = true
        setSentry(id, email, PID)
        setYM(id, email, PID)
      }
    }
  }

  /**
   * Perform logout request to API
   *
   * @memberof mwApp/auth.service:authSvc
   * @return {Promise} promise
   */
  function doLogout () {
    if (!this.hasKeycloak()) {
      return $http.post('logout')
        .finally(
          () => {
          // try to reset credentials
            storage.remove('authenticated')
            storage.remove('user')
            // resetCredentials()
            //   .catch((err) => console.warn(err))

            setAuthenticated(false) // set authentication to false regardless of request result
          }
        )
    } else {
      // resetCredentials()
      //   .catch((err) => console.warn(err))
      setAuthenticated(false)
      return keycloakAdapterSvc.logout()
    }
  }
  function initKC (go, isSetplexLogin) {
    // TODO: here we HARDCODE 'nora' prefix. Admins says what we always use this prefix
    const basePath = `${window.location.origin}/nora/`
    const loginPage = isSetplexLogin ? 'setplex-login' : 'login'
    const url = storage.get('KEYCLOAK_URL') || (isSetplexLogin && !!window.ENV.APP_KEYCLOAK_URL_SETPLEX ? window.ENV.APP_KEYCLOAK_URL_SETPLEX : window.ENV.APP_KEYCLOAK_URL)
    const realm = storage.get('KEYCLOAK_REALM') || (isSetplexLogin ? window.ENV.APP_KEYCLOAK_REALM_SETPLEX : window.ENV.APP_KEYCLOAK_REALM)
    const clientId = storage.get('KEYCLOAK_CLIENT_ID') || (isSetplexLogin ? window.ENV.APP_KEYCLOAK_CLIENT_ID_SETPLEX : window.ENV.APP_KEYCLOAK_CLIENT_ID)

    return keycloakAdapterSvc.init(
      url,
      realm,
      clientId,
      `${basePath}${loginPage}`,
      go)
      .then(function (authorized) {
        storage.set('KEYCLOAK_REALM', realm)
        storage.set('KEYCLOAK_CLIENT_ID', clientId)
        storage.set('KEYCLOAK_URL', url)

        keycloakAdapterSvc.setAuthorized(authorized)
        const token = keycloakAdapterSvc.getToken()
        storage.set('KEYCLOAK_TOKEN', token)
        $rootScope.$emit('event:keycloakInited')
        init()
      }).catch(() => {
        const path = window.location.pathname.replace(/^\/nora/, '')
        $state.go('login', { go: path })
      })
  }

  function keycloakInitEventSubscribe (go, isSetplexLogin) {
    angular.element(document).ready(() => {
      initKC(go, isSetplexLogin)
    })
  }

  /**
   * Perform request to API to restore password
   *
   * @memberof mwApp/auth.service:authSvc
   * @param {Object} user - object with field 'email'
   * @param {String} user.email - users's e-mail
   * @return {Promise} promise
   */
  function restorePassword (user) {
    return whenTimezone
      .then(
        () => // do restore password request
          $http.post('api/user/lost', user)
      )
  }

  /**
   * Perform request to API to change password
   *
   * @memberof mwApp/auth.service:authSvc
   * @param {Object} pwd - object with fields 'oldPwd' and 'newPwd'
   * @param {String} pwd.oldPwd - users's current password
   * @param {String} pwd.newPwd - users's new password
   * @return {Promise} promise
   */
  function changePassword (pwd) {
    return $http.put('api/login/password', pwd)
  }

  /**
   * Perform request to API to reset password
   *
   * @memberof mwApp/auth.service:authSvc
   * @param newPwd
   * @param {String} token - users's confirmation token
   * @return {Promise} promise
   */
  function resetPassword (newPwd, token) {
    return $http.post(`api/user/password-reset-confirm/${token}`, { newPwd })
  }

  /**
   * Perform request to API to reset password
   *
   * @memberof mwApp/auth.service:authSvc
   * @param {String} token - users's confirmation token
   * @return {Promise} promise
   */
  function resetTokenConfirm (token) {
    return $http.get(`api/user/password-reset-confirm/${token}`)
  }

  /**
   * Check, if user has access according to given access string
   *
   * @memberof mwApp/auth.service:authSvc
   * @param  {String} access - access string
   * @param  {Object} params - parameters for access string
   * @return {Boolean}
   */
  function hasAccess (access, params) {
    return accessParser(access).check(params)
  }

  /** Credential API **/

  /**
   * Check, if browser has Credential API support
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Credential_Management_API
   * @see https://developers.google.com/web/fundamentals/security/credential-management/
   */
  function hasCredentialAPI () {
    return !!(navigator.credentials && navigator.credentials.preventSilentAccess)
  }

  /**
   * Try to authenticate with new Credentials API
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function getCredentials () {
    return hasCredentialAPI()
      ? navigator.credentials.get({ password: true, mediation: 'silent' })
        .then(
          (credentials) =>
            credentials
              ? { username: credentials.id, password: credentials.password }
              : $q.reject('No credentials')
        )
      : $q.reject('Credentials API is not supported')
  }

  /**
   * Save credentials with new Credentials API
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function saveCredentials ({ username, password }) {
    return hasCredentialAPI()
      ? navigator.credentials.create({ password: { id: username, password } })
        .then(credentials => navigator.credentials.store(credentials))
      : $q.reject('Credentials API is not supported')
  }

  /**
   * Prevent silent authentication with new Credentials API
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function resetCredentials () {
    return hasCredentialAPI()
      ? navigator.credentials.preventSilentAccess()
      : $q.reject('Credentials API is not supported')
  }

  /**
   * Check, if SSO script is injected and available
   */
  function hasKeycloak () {
    return !!(window.ENV.APP_KEYCLOAK_ENABLED)
  }

  /**
   * Init future flags request
   * Get server future flags map
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function manageFeatureFlags (force = false) {
    let flags = storage.get('features', null)
    featureFlagsParser.setFlags(flags)

    if (force) {
      $http.get('api/features')
        .then(
          response => {
            flags = response.data.payload
            storage.set('features', flags) // save flags to storage
            featureFlagsParser.setFlags(flags)
          }
        )
    }
  }

  /**
   * Init age ratings and countries request
   * Get server age ratings and countries map
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function manageAgeRatings (force = false) {
    if (force) {
      const countries = $http.get('api/countries')
      const ageRatings = $http.get('api/age-ratings')
      Promise.all([countries, ageRatings]).then((results) => {
        const defaultAgeRatings = []
        results[1].data.payload.filter((ar) => ar.country === null).forEach((ar) => {
          const { rating, id, sortOrder } = ar
          defaultAgeRatings[sortOrder] = { id, rating }
        })
        const customAgeRatings = []
        const countries = []
        results[0].data.payload.forEach((c) => {
          const ageRatingsForCountry = []
          results[1].data.payload.filter((ar) => {
            if (ar.country) {
              return ar.country.id === c.id
            }
          }).forEach((ar) => {
            const { rating, id, sortOrder } = ar
            ageRatingsForCountry[sortOrder] = { id, rating }
          })
          if (ageRatingsForCountry.length) {
            customAgeRatings.push({ ...c, ageRatings: ageRatingsForCountry.filter(a => a !== null) })
            countries.push(c)
          }
        })
        const ageRatings = {
          defaultAgeRatings,
          customAgeRatings
        }
        storage.set('countries', countries)
        storage.set('ageRatings', ageRatings)
      })
    }
  }
  /**
   * Init currencies request
   * Get currencies
   *
   * @memberof mwApp/auth.service:authSvc
   */
  function manageCurrencies (force = false) {
    if (hasAccess('viewCurrency') && force) {
      $http.get('api/currencies').then(result => {
        storage.set('currencies', result.data)
      })
    }
  }

  /**
   * Check has enabled flag
   *
   * @memberof mwApp/auth.service:authSvc
   * @param  {String} flag - flag string
   * @return {Boolean}
   */
  function hasEnabledFlag (flag) {
    return featureFlagsParser.check(flag)
  }
}
